import { useMemo, useState } from 'react';
import { Class, DIVISION_STYLE, RegisteredEvent } from '@type/db-types';
import {
    CircularProgress,
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent,
    Typography,
} from '@mui/material';
import invariant from 'tiny-invariant';
import useEventContext from '@hooks/useEventContext';
import { updateRegistrantClass } from '@modules/supabase/events';
import MadisonSystemInfoAlert from './MadisonSystemInfoAlert';
import { getClassLabel } from '@utils/event-utils';

export default function ClassSelector({
    registrant,
    refresh,
    enabled, // for when not editing so we can unify division fetch & madison System check here...
}: {
    refresh: () => void;
    registrant: RegisteredEvent;
    enabled: boolean;
}) {
    const { classId, divisionId, membershipId } = registrant;
    const { event } = useEventContext();
    const [loading, setLoading] = useState(false);

    const division = useMemo(
        () => event?.divisions.find((d) => d.id === divisionId),
        [event?.divisions, divisionId],
    );

    const options: Class[] = useMemo(() => division?.classes || [], [division?.classes]);

    const handleChange = async ({ target: { value: newClassId } }: SelectChangeEvent<string>) => {
        setLoading(true);

        invariant(membershipId, '[ClassSelector] missing membership entry');

        await updateRegistrantClass(divisionId, newClassId, membershipId);

        // invalidate local user data & refetch
        refresh();
        setLoading(false);
    };

    if (loading) return <CircularProgress />;

    if (division?.bracketStyle === DIVISION_STYLE.MADISON_SYSTEM) return <MadisonSystemInfoAlert />;

    if (!enabled) return getClassLabel(undefined, classId, event) || 'n/a';

    return (
        <FormControl fullWidth>
            <Select
                id="permission-level-select"
                value={classId || ''}
                onChange={handleChange}
                // padding right to ensure the dropdown arrow does not overlap the text
                SelectDisplayProps={{ style: { padding: '0.5rem', paddingRight: '1.75rem' } }}
            >
                {options.map((cls: Class) => (
                    <MenuItem value={cls.id} key={cls.id}>
                        <Typography variant="body2">{getClassLabel(cls)}</Typography>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
